import { useFilter } from '@/contexts/FiltersContext.context';
import { resolveIconUrl } from '@/utils/iconResolver';
import './CheckboxGroup.scss';
import checkIcon from '@/assets/icons/check.svg';

export type CheckboxVariant = 'icon' | 'text';
export type IconHoverStyle = 'replace' | 'overlay';

export interface CheckboxOption {
  value: string;
  label: string;
  icon?: string;
  iconMaxWidth?: number;
}

export interface CheckboxGroupProps {
  name: string;
  label?: string;
  variant?: CheckboxVariant;
  iconHoverStyle?: IconHoverStyle;
  options: CheckboxOption[];
  values?: string[];
  defaultValues?: string[];
  onChange?: (values: string[]) => void;
}

export function CheckboxGroup({
  name,
  label,
  variant = 'icon',
  iconHoverStyle = 'replace',
  options = [],
  values,
  defaultValues = [],
  onChange,
}: CheckboxGroupProps) {
  const [contextValue, setContextValue] = useFilter(name);

  const currentValues = values ?? (Array.isArray(contextValue) ? contextValue : defaultValues);

  const handleChange = (value: string) => {
    const newSelected = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    setContextValue(newSelected);
    onChange?.(newSelected);
  };

  const isChecked = (value: string) => currentValues.includes(value);

  const renderOptionContent = (option: CheckboxOption, checked: boolean) => {
    switch (variant) {
      case 'text':
        return (
          <>
            {checked && (
              <span className="checkbox-group__text-check">
                <img src={checkIcon} alt="" width={16} height={16} />
              </span>
            )}
            <span className="checkbox-group__text">{option.label}</span>
          </>
        );

      case 'icon':
      default: {
        // iconHoverStyle: "replace" = the icon disappears and the checkmark replaces it
        // iconHoverStyle: "overlay" = the icon stays visible with the checkmark overlaid
        const hideIconWhenChecked = iconHoverStyle === 'replace';
        return (
          <>
            <span className="checkbox-group__icon">
              {option.icon && (
                <img
                  src={resolveIconUrl(option.icon)}
                  alt=""
                  width={16}
                  height={16}
                  className={`checkbox-group__icon-default${hideIconWhenChecked && checked ? ' checkbox-group__icon--hidden' : ''}`}
                  style={option.iconMaxWidth ? { maxWidth: `${option.iconMaxWidth}px` } : undefined}
                />
              )}
              <img
                src={checkIcon}
                alt=""
                width={16}
                height={16}
                className={`checkbox-group__icon-check${!checked ? ' checkbox-group__icon--hidden' : ''}`}
              />
            </span>
            <span className="checkbox-group__text">{option.label}</span>
          </>
        );
      }
    }
  };

  return (
    <div className={`checkbox-group checkbox-group--${variant}${!label ? ' checkbox-group--no-label' : ''}`}>
      {label && <label className="checkbox-group__label font-bold">{label}</label>}
      <div className="checkbox-group__options">
        {options.map((option) => {
          const checked = isChecked(option.value);
          return (
            <label
              key={option.value}
              className={`checkbox-group__option rounded-full${checked ? ' checkbox-group__option--active' : ''}`}
            >
              <input
                type="checkbox"
                name={name}
                value={option.value}
                checked={checked}
                onChange={() => handleChange(option.value)}
                className="checkbox-group__input"
              />
              {renderOptionContent(option, checked)}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default CheckboxGroup;
